import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone, FileSpreadsheet, Zap, Globe, ShieldCheck,
  Code2, Upload, BarChart3, MessageCircle, CheckCircle2,
  ArrowRight, Send, ChevronDown, CreditCard, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LoginDialog } from "@/components/LoginDialog";

const LOGO_URL = "/veritel-logo.jpeg";

const LandingPage = () => {
  const navigate = useNavigate();
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMsg, setContactMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const handleLogin = () => setLoginOpen(true);

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMsg.trim()) return toast.error("Completa nombre, email y mensaje");
    setSending(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: contactName.trim(), email: contactEmail.trim(), phone: contactPhone.trim(), message: contactMsg.trim() }),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success(data.message || "Mensaje enviado");
        setContactName(""); setContactEmail(""); setContactPhone(""); setContactMsg("");
      } else {
        toast.error(data.detail || "Error al enviar");
      }
    } catch {
      toast.error("Error de conexion");
    } finally {
      setSending(false);
    }
  };

  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plans`)
      .then(r => r.json())
      .then(d => setPlans((d.plans || []).filter(p => p.active)))
      .catch(() => {});
  }, []);

  const features = [
    { icon: Phone, title: "Verificacion de Telefonos", desc: "Normalizacion E.164, deteccion movil/fijo, validacion de estructura y analisis para Argentina." },
    { icon: MessageCircle, title: "Verificacion de WhatsApp", desc: "Detecta que numeros tienen WhatsApp activo. Exporta los resultados segmentados." },
    { icon: FileSpreadsheet, title: "Procesamiento Masivo", desc: "Subi archivos CSV, TXT o Excel para verificar miles de contactos de una sola vez." },
    { icon: Code2, title: "API REST", desc: "Integra Veritel en tu CRM o sistema existente con nuestra API documentada y API Keys dedicadas." },
    { icon: BarChart3, title: "Dashboard en Tiempo Real", desc: "Visualiza campanas, saldo consumido y distribucion de resultados al instante." },
    { icon: ShieldCheck, title: "Multi-Empresa", desc: "Gestion de multiples empresas con creditos y configuraciones independientes." },
  ];

  const steps = [
    { num: "01", title: "Subi tu archivo", desc: "Carga un CSV, TXT o Excel con tus contactos. Tambien podes enviar archivos por WhatsApp." },
    { num: "02", title: "Procesamiento automatico", desc: "Veritel analiza cada contacto: normaliza, clasifica y verifica en segundos." },
    { num: "03", title: "Resultados listos", desc: "Descarga los resultados filtrados. Celulares, fijos, invalidos y WhatsApp en un click." },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] noise-overlay">
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090B]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Veritel" className="w-9 h-9" />
            <span className="font-black text-xl text-white tracking-tight">Veritel</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#features" className="text-zinc-400 hover:text-white transition-colors">Funcionalidades</a>
            <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors">Precios</a>
            <a href="#how" className="text-zinc-400 hover:text-white transition-colors">Como funciona</a>
            <a href="#api" className="text-zinc-400 hover:text-white transition-colors">API</a>
            <a href="#contact" className="text-zinc-400 hover:text-white transition-colors">Contacto</a>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="nav-login-btn" onClick={handleLogin} className="bg-violet-600 hover:bg-violet-700 text-white border border-violet-500/50 px-5">
              Iniciar Sesion
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-600/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
              <ShieldCheck className="w-4 h-4 text-violet-400" />
              <span className="text-xs font-semibold text-violet-300 tracking-wide">Verificacion inteligente de contactos</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9] text-white mb-6">
              VALIDA Y LIMPIA
              <br />
              <span className="bg-gradient-to-r from-violet-400 via-violet-500 to-purple-600 bg-clip-text text-transparent">TUS CONTACTOS</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Verifica telefonos y WhatsApp de forma masiva.
              Integra con tu sistema via API. Procesa archivos desde la web.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="hero-get-started-btn" onClick={handleLogin} size="lg" className="bg-violet-600 hover:bg-violet-700 text-white border border-violet-500/50 px-8 py-6 text-lg group">
                Comenzar ahora <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button data-testid="hero-api-btn" variant="outline" size="lg" className="border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300 hover:text-white px-8 py-6 text-lg" onClick={() => document.getElementById("api")?.scrollIntoView({ behavior: "smooth" })}>
                <Code2 className="w-5 h-5 mr-2" />Ver API
              </Button>
            </div>
          </div>
          <a href="#features" className="flex justify-center mt-8 animate-bounce">
            <ChevronDown className="w-6 h-6 text-zinc-600" />
          </a>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-violet-400 mb-3">Funcionalidades</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Todo lo que necesitas</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={i} className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 hover:border-violet-500/30 transition-colors backdrop-blur-sm group">
                <div className="w-11 h-11 rounded-lg bg-violet-600/15 flex items-center justify-center mb-4 text-violet-400 group-hover:bg-violet-600/25 transition-colors">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 border-t border-white/5" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-violet-400 mb-3">Precios</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Planes simples y transparentes</h2>
            <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Paga solo por lo que usas. Sin costos ocultos.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const isFree = plan.price === 0;
              const isBest = plan.plan_id === "business";
              const totalIva = isFree ? 0 : Math.round(plan.price * 1.21);
              return (
                <div key={plan.plan_id} className={`relative rounded-xl p-6 border transition-all ${isBest ? "bg-violet-600/10 border-violet-500/40 ring-1 ring-violet-500/20" : "bg-zinc-900/50 border-white/10 hover:border-violet-500/20"}`} data-testid={`plan-${plan.plan_id}`}>
                  {isBest && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-violet-600 text-white border-0 text-[10px] px-3"><Sparkles className="w-3 h-3 mr-1" />Popular</Badge>
                    </div>
                  )}
                  {plan.discount_pct > 0 && (
                    <Badge className="absolute top-3 right-3 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">-{plan.discount_pct}%</Badge>
                  )}
                  <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                  {isFree ? (
                    <div className="mb-4">
                      <span className="text-3xl font-black text-white">Gratis</span>
                      <p className="text-xs text-zinc-500 mt-1">${plan.balance.toLocaleString("es-AR")} de saldo inicial</p>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <span className="text-3xl font-black text-white">${plan.price.toLocaleString("es-AR")}</span>
                      <span className="text-sm text-zinc-500 ml-1">+ IVA</span>
                      <p className="text-xs text-zinc-500 mt-1">Total: ${totalIva.toLocaleString("es-AR")}</p>
                    </div>
                  )}
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />Telefono</span>
                      <span className="text-white font-semibold">${plan.phone_price.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" />WhatsApp</span>
                      <span className="text-white font-semibold">${plan.wa_price.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-zinc-800 pt-2 mt-2">
                      <p className="text-xs text-zinc-500">Saldo: <span className="text-white font-semibold">${plan.balance.toLocaleString("es-AR")}</span></p>
                      {!isFree && <p className="text-[10px] text-zinc-600 mt-0.5">~{Math.floor(plan.balance / plan.phone_price).toLocaleString("es-AR")} tel. o ~{Math.floor(plan.balance / plan.wa_price).toLocaleString("es-AR")} WA</p>}
                    </div>
                  </div>
                  <Button onClick={handleLogin} className={`w-full ${isBest ? "bg-violet-600 hover:bg-violet-700 text-white" : isFree ? "bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700" : "bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"}`} data-testid={`plan-${plan.plan_id}-btn`}>
                    {isFree ? "Probar gratis" : "Comenzar"}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-zinc-600 mt-6">Todos los precios en pesos argentinos (ARS). IVA 21% no incluido en los precios base.</p>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-zinc-500 mb-3">Proceso</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Como funciona</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-violet-600/10 mb-2">{s.num}</div>
                <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                {i < 2 && <div className="hidden md:block absolute top-8 right-0 w-8 h-0.5 bg-violet-600/20 translate-x-4" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Section */}
      <section id="api" className="py-20 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase text-zinc-500 mb-3">Integracion</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">API REST</h2>
            <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Integra la verificacion de contactos en tu CRM o sistema via <code className="text-violet-400 bg-zinc-800 px-1.5 py-0.5 rounded text-xs">veritel.com.ar/api</code></p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-zinc-900/70 border border-white/10 rounded-lg p-5 font-mono text-sm overflow-x-auto">
              <p className="text-zinc-500 mb-2">// Verificacion de Telefono</p>
              <pre className="text-zinc-300 whitespace-pre-wrap">{`{
  "input": "3815551234",
  "normalized": "+5493815551234",
  "country": "AR",
  "is_valid": true,
  "type": "mobile",
  "provincia": "Tucuman",
  "localidad": "SAN MIGUEL DE TUCUMAN",
  "reasons": [
    "valid_structure",
    "enacom_database_match"
  ]
}`}</pre>
            </div>
            <div className="bg-zinc-900/70 border border-white/10 rounded-lg p-5 font-mono text-sm overflow-x-auto">
              <p className="text-zinc-500 mb-2">// Verificacion de WhatsApp</p>
              <pre className="text-zinc-300 whitespace-pre-wrap">{`{
  "number": "+5491155001234",
  "has_whatsapp": true,
  "push_name": "Juan",
  "verified_at": "2026-03-14T10:30:00Z"
}`}</pre>
            </div>
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {[
              { icon: CheckCircle2, text: "API Key dedicada por empresa" },
              { icon: CheckCircle2, text: "Respuestas en JSON estructurado" },
              { icon: CheckCircle2, text: "Rate limiting configurable" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-zinc-300">
                <item.icon className="w-4 h-4 text-emerald-500 shrink-0" />
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-zinc-500 mb-3">Contacto</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Hablemos</h2>
            <p className="text-zinc-400 mt-3">Escribinos y te respondemos a la brevedad.</p>
          </div>
          <form onSubmit={handleContact} className="space-y-4 bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Nombre" className="bg-zinc-950 border-zinc-800 text-white" data-testid="contact-name" />
              <Input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="Email" type="email" className="bg-zinc-950 border-zinc-800 text-white" data-testid="contact-email" />
            </div>
            <Input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="Telefono (ej: +549381...)" className="bg-zinc-950 border-zinc-800 text-white" data-testid="contact-phone" />
            <textarea value={contactMsg} onChange={e => setContactMsg(e.target.value)} placeholder="Mensaje" rows={4} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm resize-y" data-testid="contact-message" />
            <Button type="submit" disabled={sending} className="bg-violet-600 hover:bg-violet-700 text-white w-full" data-testid="contact-submit">
              <Send className="w-4 h-4 mr-2" />{sending ? "Enviando..." : "Enviar mensaje"}
            </Button>
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">Empeza a verificar hoy</h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">Mejora la calidad de tus leads y optimiza tus campanas con verificacion inteligente.</p>
          <Button data-testid="cta-get-started-btn" onClick={handleLogin} size="lg" className="bg-violet-600 hover:bg-violet-700 text-white border border-violet-500/50 px-10 py-6 text-lg">
            Crear Cuenta
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          {/* Top: brand + fiscal + contact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src={LOGO_URL} alt="Veritel" className="w-7 h-7" />
                <span className="font-black text-lg text-white tracking-tight">Veritel</span>
              </div>
              <p className="text-sm text-zinc-400 max-w-xs">
                Plataforma de verificacion inteligente de contactos. Telefonos, emails y WhatsApp.
              </p>
            </div>

            {/* Datos fiscales */}
            <div data-testid="fiscal-info">
              <h4 className="text-xs font-bold tracking-widest text-violet-400 uppercase mb-3">Datos fiscales</h4>
              <ul className="text-sm text-zinc-400 space-y-1.5">
                <li className="text-zinc-300 font-semibold">ANDRES CARLOS OSTENGO</li>
                <li><span className="text-zinc-500">CUIT:</span> 20-27575122-8</li>
                <li className="leading-relaxed">
                  <span className="text-zinc-500">Domicilio legal:</span> San Martin 839, Piso 3 Dpto A
                  <span className="block text-zinc-500 text-xs">Entre Salta y Junin - San Miguel de Tucuman, Argentina</span>
                </li>
              </ul>
            </div>

            {/* Contacto */}
            <div data-testid="contact-info">
              <h4 className="text-xs font-bold tracking-widest text-violet-400 uppercase mb-3">Contacto</h4>
              <ul className="text-sm text-zinc-400 space-y-1.5">
                <li>
                  <span className="text-zinc-500">WhatsApp:</span>{" "}
                  <a href="https://wa.me/5491124510886" target="_blank" rel="noopener noreferrer" className="hover:text-violet-400 transition-colors">+54 9 11 2451-0886</a>
                </li>
                <li>
                  <span className="text-zinc-500">Tel:</span>{" "}
                  <a href="tel:08101228899" className="hover:text-violet-400 transition-colors">0810-122-8899</a>
                </li>
                <li>
                  <span className="text-zinc-500">Email:</span>{" "}
                  <a href="mailto:veritelwhat@gmail.com" className="hover:text-violet-400 transition-colors">veritelwhat@gmail.com</a>
                </li>
                <li className="leading-relaxed">
                  <span className="text-zinc-500">Direccion:</span> San Martin 839 - 3 A
                  <span className="block text-zinc-500 text-xs">San Miguel de Tucuman, Argentina</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom: legal + copyright */}
          <div className="pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <a href="/terms" className="hover:text-zinc-300 transition-colors">Terminos y Condiciones</a>
              <a href="/privacy" className="hover:text-zinc-300 transition-colors">Politica de Privacidad</a>
              <a href="/data-deletion" className="hover:text-zinc-300 transition-colors">Eliminacion de Datos</a>
            </div>
            <p className="text-xs text-zinc-600">veritel.com.ar - 2026 Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
